import { DEFAULT_LOCALE, PSEUDO_LOCALE, type AnyLocaleType } from '@featit/shared/i18n/core';
import * as entry from '@featit/shared/i18n/messages/en/entry';
import type { en } from '@featit/shared/i18n/messages/index';
import { reachWords } from 'virtual:featit-i18n-reach-words';
import { I18N_TEST_LOCALES } from '@/buildConstants';

/**
 * Which catalogue a language reads, and how it gets here (ADR-148, concept §5.5).
 *
 * - English is the source and the fallback. The areas the first screens draw are imported statically
 *   (`messages/en/entry.ts`), so a first paint in English costs nothing new; every other area is registered by
 *   the chunk that draws it (`registerEnglishArea`), so it weighs on that chunk and never on the entry. It is
 *   precompiled (`web/i18nCataloguePlugin.ts`), so no ICU parser ships with it.
 * - Every other language is its own chunk, loaded on first use with `import()` and never reached from the
 *   entry's static graph (`check:first-screen` holds that, IN-3).
 * - The pseudo-locale is offered only in a build that asks for test locales.
 */
const englishAreas: Record<string, unknown> = { ...entry };
export const ENGLISH = {
  locale: DEFAULT_LOCALE as AnyLocaleType,
  messages: englishAreas as unknown,
};

/**
 * A screen's chunk puts its own English area in the catalogue when it loads, before it draws:
 * `registerEnglishArea('settings', settings)`. The key is checked against the full catalogue's type.
 */
export function registerEnglishArea<K extends keyof typeof en>(
  name: K,
  area: (typeof en)[K],
): void {
  englishAreas[name] = area;
}

/** The messages of every language already loaded, so a provider can start from them (I-2088). */
const loadedByLocale: Record<string, unknown> = { en: englishAreas };

/**
 * The messages of `locale` when they are already here, else `undefined`: English always, another
 * language once `loadMessages` has returned for it. A provider that starts from them draws the
 * person's language in its first frame, never English first.
 */
export function loadedMessages(locale: AnyLocaleType): unknown {
  return loadedByLocale[locale];
}

/**
 * I-1971: the shell's words for "FEATit cannot be reached" in `locale`, which the entry carries
 * (`shared/src/i18n/reach-words.ts`), or `undefined` for English and a language without them. What a
 * page shows while its language's chunk could not be fetched.
 */
export function reachMessages(locale: AnyLocaleType): unknown {
  return locale === DEFAULT_LOCALE ? undefined : reachWords[locale];
}

export async function loadMessages(locale: AnyLocaleType): Promise<unknown> {
  return (loadedByLocale[locale] ??= await readMessages(locale));
}

async function readMessages(locale: AnyLocaleType): Promise<unknown> {
  if (locale === 'de') {
    // German messages may use plural and select: the formatter comes with them.
    await import('./heavy');
    return (await import('@featit/shared/i18n/messages/de/index')).de;
  }
  if (I18N_TEST_LOCALES && locale === PSEUDO_LOCALE) {
    return (await import('virtual:featit-i18n-pseudo')).pseudo;
  }
  return englishAreas;
}
